import { command } from "./lib/command";
import { Component, useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { ConnectionPill, ToastHost, TooltipHost } from "./components/Chrome";
import { ListPane } from "./components/List";
import { HelpModal, Palette, SettingsModal, closeModal } from "./components/Modals";
import { Prefetch, Reader } from "./components/Reader";
import { Sidebar } from "./components/Sidebar";
import { SignIn } from "./components/SignIn";
import { api, useOwnerMutation } from "./lib/api";
import { useSettings } from "./lib/library";
import { asTransition } from "./lib/transition";
import { readingLabel, useArticleActions, useFeeds } from "./lib/library";
import { getPrefs, lookOf, onLookChange, setPrefs, SYNCED, toggleNav, usePrefs } from "./lib/prefs";
import { optimisticLook } from "./lib/settings";
import { errorCode, setToken, useToken } from "./lib/session";
import { READ_AFTER, openReading, extendReading, closeReading, getState, landing, subscribe, openSettings, rowCache, setView, toast, update, useStore, withOverlay, type Row, clearPicks, pickAll, pickStep } from "./lib/store";
import { startVisit } from "./lib/readClock";
import { nativeControlKey } from "./lib/keyboard";
import { ListenBar, ListenProvider } from "./components/Listen";

/** Sends the owner back to sign-in when the server says the session ended. */
class SessionBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch(e: unknown) {
    if (errorCode(e) === "SIGNED_OUT" || String(e).includes("SIGNED_OUT")) setToken(null);
  }
  render() {
    if (!this.state.failed) return this.props.children;
    return <div className="empty full"><h2>Something went wrong</h2><p>Reload the page to try again.</p><button className="btn" onClick={() => location.reload()}>Reload</button></div>;
  }
}

export function App() {
  const token = useToken();
  return token ? <SessionBoundary key={token}><Shell /></SessionBoundary> : <SignIn />;
}

function Shell() {
  const prefs = usePrefs();
  const reading = useStore((st) => st.reading);
  const focus = useStore((st) => st.focus);
  const peek = useStore((st) => st.peek);
  // picks belong to the list they were made in
  const scopeNow = useStore((st) => st.scope), filterNow = useStore((st) => st.filter);
  useEffect(() => clearPicks(), [scopeNow, filterNow]);
  const selected = useStore((st) => st.selected);
  const modal = useStore((st) => st.modal);
  const scope = useStore((st) => st.scope);
  const feeds = useFeeds();
  const { read, save, markAll } = useArticleActions();
  const checkNow = useOwnerMutation(api.feeds.checkNow);
  const renew = useOwnerMutation(api.auth.renew);
  const [drawer, setDrawer] = useState(false);
  const rows = useRef<Row[]>([]);
  const label = readingLabel(scope, feeds);
  const lastPage = useRef({ key: "", revision: 0 });
  const setRows = useCallback((r: Row[], pageRevision: number, key: string) => {
    rows.current = r;
    const st = getState().reading;
    const paged = key === lastPage.current.key && pageRevision > lastPage.current.revision;
    lastPage.current = { key, revision: pageRevision };
    if (!st) return;
    extendReading(r.map((x) => x.id), paged, label);
  }, [label]);

  useEffect(() => { renew({}).catch((e) => { if (errorCode(e) === "SIGNED_OUT") setToken(null); }); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // the open article counts as read after a moment on screen (lib/readClock.ts says when)
  const readingId = reading?.id;
  const readNow = useRef(read);
  readNow.current = read;
  useEffect(() => {
    if (!readingId) return;
    update({ keep: new Set(getState().keep).add(readingId) });
    return startVisit(readingId, {
      row: (id) => { const r = rowCache.get(id); return r && withOverlay(r); },
      pending: () => getState().pendingRead,
      setPending: (id) => update({ pendingRead: id }),
      markRead: (id) => readNow.current(id, true),
      onChange: subscribe,
      visible: () => document.visibilityState === "visible",
      onVisibilityChange: (fn) => { document.addEventListener("visibilitychange", fn); return () => document.removeEventListener("visibilitychange", fn); },
      landing,
      after: READ_AFTER,
    });
  }, [readingId, reading?.visit]);

  const open = useCallback((id: string, ctx?: string[]) => {
    const st = getState();
    const session = ctx ?? (st.reading && st.reading.session.includes(id) ? st.reading.session : rows.current.map((r) => r.id));
    const show = () => openReading(id, session, label);
    // opening from the list animates; moving on to the next article does not
    if (st.reading) show(); else asTransition("open", show, id);
    setDrawer(false);
  }, [label]);

  const step = useCallback((d: 1 | -1) => {
    const st = getState();
    if (!st.reading) {
      // before reading: j starts reading at the first unread, k does nothing
      if (d === 1) { const first = rows.current.find((r) => !withOverlay(r).isRead); if (first) open(first.id); else toast(`Nothing unread in ${label}`); }
      return;
    }
    const { session, id } = st.reading;
    const i = session.indexOf(id), nextId = session[i + d];
    if (!nextId) { if (d === 1) toast(`End of ${st.reading.label}`); return; }
    open(nextId);
  }, [open, label]);

  const close = useCallback(() => {
    const st = getState();
    asTransition("close", closeReading, st.reading?.id);
    requestAnimationFrame(() => document.querySelector<HTMLElement>(".lscroll")?.focus({ preventScroll: true }));
  }, []);

  const refresh = useCallback(() => { void checkNow({}).then(() => toast("Check requested for every source", undefined, "refresh"), () => toast("Couldn't request a check · try again")); }, [checkNow]);
  useEffect(() => {
    const r = () => refresh();
    const g = (e: Event) => setView({ type: "feed", feedId: (e as CustomEvent<string>).detail });
    addEventListener("vladrss:refresh", r); addEventListener("vladrss:go-feed", g);
    return () => { removeEventListener("vladrss:refresh", r); removeEventListener("vladrss:go-feed", g); };
  }, [refresh]);

  // ---------- keyboard ----------
  const gAt = useRef(0);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented) return;
      const t = e.target instanceof Element ? e.target : document.body;
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); update({ modal: getState().modal === "palette" ? null : "palette" }); return; }
      // with rows picked: Esc lets go, Ctrl/⌘+A takes every row in the list
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "a" && !t.closest("input, textarea, select, [contenteditable]") && !getState().reading && !getState().modal) { e.preventDefault(); pickAll(); return; }
      if (e.key === "Escape" && getState().picks.picked.size && !getState().modal) { clearPicks(); return; }
      if (getState().modal || e.metaKey || e.ctrlKey || e.altKey) return;
      if (t.closest("input, textarea, select, [contenteditable]")) return;
      if (nativeControlKey(t, e.key)) return;
      const st = getState();
      const cur = st.reading?.id ?? st.selected;
      const row = cur ? rowCache.get(cur) : undefined;
      // a letter is the shortcut Shift says, whatever Caps Lock says
      const k = /^[a-z]$/i.test(e.key) ? (e.shiftKey ? e.key.toUpperCase() : e.key.toLowerCase()) : e.key;
      if (Date.now() - gAt.current < 900) {
        gAt.current = 0;
        if (k === "a") return setView({ type: "all" });
        if (k === "s") return setView(st.scope.type === "recent" || st.scope.type === "search" ? { type: "all" } : st.scope, "saved");
        if (k === "r") return setView({ type: "recent" });
      }
      switch (k) {
        // in the list, arrows move the highlight, and with Shift they pick, as in a spreadsheet
        case "j": case "ArrowDown": if (k === "ArrowDown" && !st.reading) { e.preventDefault(); return e.shiftKey ? pickStep(1) : moveSel(1); } e.preventDefault(); return step(1);
        case "k": case "ArrowUp": if (k === "ArrowUp" && !st.reading) { e.preventDefault(); return e.shiftKey ? pickStep(-1) : moveSel(-1); } e.preventDefault(); return step(-1);
        case " ": {
          if (!st.reading) return;
          e.preventDefault();
          const sc = document.querySelector<HTMLElement>("[data-reader]");
          if (!sc) return;
          const dir = e.shiftKey ? -1 : 1;
          if (dir > 0 && sc.scrollTop + sc.clientHeight >= sc.scrollHeight - 6) return step(1);
          sc.scrollBy({ top: dir * sc.clientHeight * 0.86, behavior: "smooth" });
          return;
        }
        case "o": case "Enter": if (!st.reading && st.selected) { e.preventDefault(); open(st.selected); } return;
        case "Escape": if (drawer) { setDrawer(false); return; } if (st.focus) return update({ focus: false }); if (st.reading) return close(); return;
        case "m": if (!row) return; if (getState().pendingRead === row.id) update({ pendingRead: null }); else read(row.id, !withOverlay(row).isRead); return;
        case "s": if (row) save(row.id, !withOverlay(row).isSaved); return;
        case "v": if (row) window.open(row.url, "_blank", "noopener"); return;
        case "A": if (e.shiftKey) void markAll(label); return;
        case "O": if (e.shiftKey) setPrefs({ order: getPrefs().order === "oldest" ? "newest" : "oldest" }); return;
        case "1": return setPrefs({ density: "compact" });
        case "2": return setPrefs({ density: "previews" });
        case "f": if (st.reading) update({ focus: !st.focus }); return;
        // the next and previous section of a long article (components/Outline.tsx)
        case "n": case "p": if (st.reading) dispatchEvent(new CustomEvent("vladrss:section", { detail: k === "n" ? 1 : -1 })); return;
        // the discussion, the last section of an article that has one (components/Discussion.tsx)
        case "c": if (st.reading) dispatchEvent(new CustomEvent("vladrss:discussion")); return;
        case "[": if (matchMedia("(max-width: 1279px)").matches) setDrawer((d) => !d); else toggleNav(); return;
        case "\\": if (st.reading) setPrefs({ listOff: !getPrefs().listOff }); return;
        case "g": gAt.current = Date.now(); return;
        case "r": return refresh();
        case "z": { const tt = st.toast; if (tt?.undo) { tt.undo(); update({ toast: null }); } return; }
        case ",": return openSettings("reading");
        case "?": return update({ modal: "help" });
      }
    };
    const moveSel = (d: number) => {
      const ids = rows.current.map((r) => r.id);
      if (!ids.length) return;
      const i = ids.indexOf(getState().selected ?? "");
      clearPicks(); // moving on without Shift lets go of the picks, as in a spreadsheet
      update({ selected: ids[Math.max(0, Math.min(ids.length - 1, i < 0 ? 0 : i + d))] ?? null });
    };
    addEventListener("keydown", onKey);
    return () => removeEventListener("keydown", onKey);
  }, [step, open, close, read, save, markAll, refresh, label, drawer]);

  // theme, colours, order and grouping follow the owner between devices
  const settings = useSettings();
  const setLook = useOwnerMutation(api.settings.patchLook, optimisticLook);
  useEffect(() => {
    const send = (look: Parameters<typeof setLook>[0]["look"]): Promise<unknown> => command(() => setLook({ look }), "Couldn't sync appearance", () => { void send(look); });
    onLookChange(send);
    return () => onLookChange(null);
  }, [setLook]);
  useEffect(() => {
    const look = settings?.look;
    if (!look) return;
    const mine = lookOf(getPrefs());
    if (SYNCED.some((k) => mine[k] !== look[k])) setPrefs(look as Partial<typeof mine>, true);
  }, [settings?.look]); // eslint-disable-line react-hooks/exhaustive-deps
  const nav = prefs.nav;
  const cls = ["w", `nav-${nav}`, reading ? "rd-open" : "", reading && prefs.listOff ? "list-off" : "", focus ? "focusmode" : "", drawer ? "drawer-open" : ""].join(" ");
  return (
    <ListenProvider open={open}><div className={cls}>
      <Sidebar onNavigate={() => setDrawer(false)} onCollapse={() => { if (matchMedia("(max-width: 1279px)").matches) setDrawer(false); else toggleNav(); }} />
      <div className="nav-scrim" onClick={() => setDrawer(false)} />
      <main className="wmain" onClickCapture={(e) => { if ((e.target as Element).closest(".navbtn") && matchMedia("(max-width: 1279px)").matches) { e.stopPropagation(); setDrawer((d) => !d); } }}>
        <ListPane onOpen={open} onStart={() => step(1)} rowsOut={setRows} />
        {reading && <Reader onNext={() => step(1)} onPrev={() => step(-1)} onClose={close} />}
        {peek && peek !== reading?.id && <Prefetch key={`peek-${peek}`} id={peek} fetchFull={false} />}
        {selected && selected !== peek && !reading && <Prefetch key={`sel-${selected}`} id={selected} fetchFull={false} />}
        <ListenBar />
      </main>
      {modal === "settings" && <SettingsModal onClose={closeModal} />}
      {modal === "help" && <HelpModal onClose={closeModal} />}
      {modal === "palette" && <Palette onClose={closeModal} onOpenArticle={open} />}
      <ToastHost />
      <ConnectionPill />
      <TooltipHost />
    </div></ListenProvider>
  );
}
